import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from '@/lib/cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 左側アイコン（lucide-react 等） */
  icon?: ReactNode;
  /** 処理中表示（自動で disabled） */
  loading?: boolean;
  block?: boolean;
}

/**
 * ボタン。type の既定は "button"（フォーム内での誤送信防止）。送信ボタンは type="submit" を明示。
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, loading = false, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('bs-btn', `bs-btn--${variant}`, `bs-btn--${size}`, block && 'bs-btn--block', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="bs-spinner bs-spinner--inline" aria-hidden /> : icon ? <span className="bs-btn__icon" aria-hidden>{icon}</span> : null}
      {children != null && <span className="bs-btn__label">{children}</span>}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** 読み上げ・ツールチップ用ラベル（必須） */
  label: string;
  icon: ReactNode;
  variant?: 'ghost' | 'secondary' | 'danger';
  size?: 'sm' | 'md';
}

/** アイコンのみのボタン。label は title と aria-label に使われる */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, variant = 'ghost', size = 'md', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx('bs-iconbtn', `bs-iconbtn--${variant}`, `bs-iconbtn--${size}`, className)}
      {...rest}
    >
      {icon}
    </button>
  );
});
